import {
  type ColorValue,
  CssColor,
  CssFont,
  type NumericValue,
  type PropertyTypeValueMap,
  StyleType,
  wixColor,
  wixFont,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParam,
  createStylesParams,
  type ISettingsColor,
  type ISettingsFont,
  type ISiteTextPreset,
  type IStyleParam,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import { settingsParamsDefinitions } from './settingsParams';
import { generateCustomCssVars } from './stylesParamsUtils';

type CssColorValue = Parameters<typeof wixColor>[0];
type CssFontValue = Parameters<typeof wixFont>[0];

export type IStylesParams = {
  topAndBottomPadding: StyleParamType.Number;
  sidePadding: StyleParamType.Number;
  widgetBorderColor: StyleParamType.Color;
  widgetBorderWidth: StyleParamType.Number;
  widgetBorderRadius: StyleParamType.Number;
  widgetBackgroundColor: StyleParamType.Color;
  headerBackgroundColor: StyleParamType.Color;
  dividerWidth: StyleParamType.Number;
  dividerColor: StyleParamType.Color;
  sectionsGap: StyleParamType.Number;
  pageTitleFont: StyleParamType.Font;
  pageTitleColor: StyleParamType.Color;
  sectionTitleFont: StyleParamType.Font;
  sectionTitleColor: StyleParamType.Color;
  primaryTextFont: StyleParamType.Font;
  primaryTextColor: StyleParamType.Color;
  secondaryTextFont: StyleParamType.Font;
  secondaryTextColor: StyleParamType.Color;
  actionColor: StyleParamType.Color;
  disabledColor: StyleParamType.Color;
  borderWidth: StyleParamType.Number;
  borderRadius: StyleParamType.Number;
  dateTextFont: StyleParamType.Font;
  dateTextColor: StyleParamType.Color;
  timeSelectionTextColor: StyleParamType.Color;
  timeSelectionTextFont: StyleParamType.Font;
  timeSelectionBackgroundColor: StyleParamType.Color;
  bookButtonTextColor: StyleParamType.Color;
  bookButtonBGColor: StyleParamType.Color;
  bookButtonBorderColor: StyleParamType.Color;
  bookButtonHoverBorderColor: StyleParamType.Color;
  bookButtonHoverTextColor: StyleParamType.Color;
  bookButtonHoverBGColor: StyleParamType.Color;
  bookButtonTextFont: StyleParamType.Font;
  slotBackgroundColor: StyleParamType.Color;
  slotPrimaryTextFont: StyleParamType.Font;
  slotPrimaryTextColor: StyleParamType.Color;
  slotNameTextFont: StyleParamType.Font;
  slotNameTextColor: StyleParamType.Color;
  slotTimeTextFont: StyleParamType.Font;
  slotTimeTextColor: StyleParamType.Color;
  slotSecondaryTextFont: StyleParamType.Font;
  slotSecondaryTextColor: StyleParamType.Color;
  slotBorderWidth: StyleParamType.Number;
  slotBorderColor: StyleParamType.Color;
  servicePrimaryTextColor0: StyleParamType.Color;
  servicePrimaryTextColor1: StyleParamType.Color;
  servicePrimaryTextColor2: StyleParamType.Color;
  servicePrimaryTextColor3: StyleParamType.Color;
  servicePrimaryTextColor4: StyleParamType.Color;
  servicePrimaryTextColor5: StyleParamType.Color;
  servicePrimaryTextColor6: StyleParamType.Color;
  servicePrimaryTextColor7: StyleParamType.Color;
  servicePrimaryTextColor8: StyleParamType.Color;
  servicePrimaryTextColor9: StyleParamType.Color;
  serviceBackgroundColor0: StyleParamType.Color;
  serviceBackgroundColor1: StyleParamType.Color;
  serviceBackgroundColor2: StyleParamType.Color;
  serviceBackgroundColor3: StyleParamType.Color;
  serviceBackgroundColor4: StyleParamType.Color;
  serviceBackgroundColor5: StyleParamType.Color;
  serviceBackgroundColor6: StyleParamType.Color;
  serviceBackgroundColor7: StyleParamType.Color;
  serviceBackgroundColor8: StyleParamType.Color;
  serviceBackgroundColor9: StyleParamType.Color;
  mobilePageTitleFontSize: StyleParamType.Number;
  mobileSectionTitleFontSize: StyleParamType.Number;
  mobilePrimaryTextFontSize: StyleParamType.Number;
  mobileSecondaryTextFontSize: StyleParamType.Number;
  mobileDateTextFontSize: StyleParamType.Number;
  mobileTimeSelectionTextFontSize: StyleParamType.Number;
  mobileBookButtonTextFontSize: StyleParamType.Number;
  mobileSlotPrimaryTextFontSize: StyleParamType.Number;
  mobileSlotTimeTextFontSize: StyleParamType.Number;
  mobileSlotNameTextFontSize: StyleParamType.Number;
  mobileSlotSecondaryTextFontSize: StyleParamType.Number;
  dataAndTimeDividerColor: StyleParamType.Color;
  serviceDetailsDividerColor: StyleParamType.Color;
  dataAndTimeDividerWidth: StyleParamType.Number;
  serviceDetailsDividerWidth: StyleParamType.Number;
  dateAndTimeSectionTitleColor: StyleParamType.Color;
  dateAndTimeSectionTitleFont: StyleParamType.Font;
  dateAndTimePrimaryTextColor: StyleParamType.Color;
  dateAndTimePrimaryTextFont: StyleParamType.Font;
  dateAndTimeSecondaryTextColor: StyleParamType.Color;
  dateAndTimeSecondaryTextFont: StyleParamType.Font;
  serviceDetailsSectionTitleColor: StyleParamType.Color;
  serviceDetailsSectionTitleFont: StyleParamType.Font;
  serviceDetailsPrimaryTextColor: StyleParamType.Color;
  serviceDetailsPrimaryTextFont: StyleParamType.Font;
  serviceDetailsSecondaryTextColor: StyleParamType.Color;
  serviceDetailsSecondaryTextFont: StyleParamType.Font;
  bookButtonCornerRadius: StyleParamType.Number;
  priceBeforeDiscountColor: StyleParamType.Color;
  priceBeforeDiscountFont: StyleParamType.Font;
  discountNameColor: StyleParamType.Color;
  discountNameFont: StyleParamType.Font;
  mobilePriceBeforeDiscountFontSize: StyleParamType.Number;
  mobileDiscountNameFontSize: StyleParamType.Number;
} & IStyleParamsBoolean &
  IStyleParamsVariants;

export type IStyleParamsVariants = {
  headerSubtitleSource: StyleParamType.Number;
};

export type IStyleParamsBoolean = {
  headerSubtitleVisibility: StyleParamType.Boolean;
  headerTitleVisibility: StyleParamType.Boolean;
  headerLocationFilterVisibility: StyleParamType.Boolean;
  headerStaffFilterVisibility: StyleParamType.Boolean;
  headerServiceFilterVisibility: StyleParamType.Boolean;
  headerVisibility: StyleParamType.Boolean;
  headerTimezoneSelectionVisibility: StyleParamType.Boolean;
  timeslotDetailsVisibility: StyleParamType.Boolean;
  videoConferenceBadgeVisibility: StyleParamType.Boolean;
  dateAndTimeTimezoneSelectionVisibility: StyleParamType.Boolean;
};

type BooleanStyleParam = Omit<
  IStyleParam<StyleParamType.Boolean, boolean>,
  'name'
>;

export const booleanParam = (
  defaultValue: boolean | undefined,
): BooleanStyleParam => ({
  type: StyleParamType.Boolean,
  getDefaultValue: () => defaultValue as boolean,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: defaultValue as boolean,
    },
  },
});

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<
    PropertyTypeValueMap[K]
  > extends Required<ValueType>
    ? K
    : never]: K;
};

export const numberParam = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number | undefined,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue as number,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'static',
      value: defaultValue as number,
    },
  },
});

export const numberParamWithMobileDefault = (
  type: StyleTypeFor<NumericValue>,
  mobileDefaultValue: number,
  defaultValue: number,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ isMobile }) =>
    isMobile ? mobileDefaultValue : defaultValue,
  inheritDesktop: false,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
    mobileDefaultValue: {
      type: 'static',
      value: mobileDefaultValue,
    },
  },
});

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

export const colorParam = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  themeColor: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
  metadata: {
    type,
    defaultValue: wixColor(themeColor),
  },
});

export const colorParamWithOpacity = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  opacity: number,
  themeColor: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
  metadata: {
    type,
    defaultValue: wixColor(themeColor),
  },
});

export const colorRefParam = (
  otherColorParam: ColorStyleParam,
  otherColorParamName: string,
  themeColor?: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherColorParam),
  metadata: {
    type: StyleType.color,
    defaultValue: themeColor
      ? wixColor(themeColor)
      : {
          type: 'ref',
          ref: otherColorParamName,
        },
  },
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

export const fontParam = (
  preset: keyof ISiteTextPreset,
  props: { size?: number; htmlTag?: string },
  themeFont: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, props),
  metadata: {
    type: StyleType.font,
    defaultValue: wixFont(themeFont),
  },
});

export const fontRefParam = (
  otherFontParam: FontStyleParam,
  otherFontParamName: string,
  themeFont?: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherFontParam),
  metadata: {
    type: StyleType.font,
    defaultValue: themeFont
      ? wixFont(themeFont)
      : {
          type: 'ref',
          ref: otherFontParamName,
        },
  },
});

export const fontSizeRefParam = (
  otherFontSizeParam: NumberStyleParam,
  otherFontSizeParamName: string,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherFontSizeParam),
  metadata: {
    type: StyleType.fontSize,
    defaultValue: {
      type: 'ref',
      ref: otherFontSizeParamName,
    },
  },
});

export const fontParamWithMobileDefault = (
  preset: keyof ISiteTextPreset,
  mobileSize: number,
  size: number,
  themeFont: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ textPresets, isMobile }) =>
    wixFontParam(preset, {
      size: isMobile ? mobileSize : size,
    })({ textPresets }),
  metadata: {
    type: StyleType.font,
    defaultValue: wixFont(themeFont),
    mobileDefaultValue: wixFont(themeFont),
  },
});

const numberRefParam = (
  otherNumberParam: NumberStyleParam,
  otherNumberParamName: string,
  type: StyleType.borderWidth | StyleType.borderRadius,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherNumberParam),
  metadata: {
    type,
    defaultValue: {
      type: 'ref',
      ref: otherNumberParamName,
    },
  },
});

const slotPrimaryTextFont = createStylesParam(
  'slotPrimaryTextFont',
  fontParam('Body-M', { size: 16 }, CssFont.WstParagraph2Font),
);

const slotPrimaryTextColor = createStylesParam(
  'slotPrimaryTextColor',
  colorParam(StyleType.color, 'color-5', CssColor.WstParagraph2Color),
);

const mobileSlotPrimaryTextFontSize = createStylesParam(
  'mobileSlotPrimaryTextFontSize',
  numberParam(StyleType.fontSize, 14),
);

const dividerColor = createStylesParam(
  'dividerColor',
  colorParam(StyleType.color, 'color-5', CssColor.WstSystemLine1Color),
);

const dividerWidth = createStylesParam(
  'dividerWidth',
  numberParam(StyleType.borderWidth, 1),
);

const sectionTitleColor = createStylesParam(
  'sectionTitleColor',
  colorParam(StyleType.color, 'color-5', CssColor.WstHeading2Color),
);

const sectionTitleFont = createStylesParam(
  'sectionTitleFont',
  fontParam('Page-title', { size: 20 }, CssFont.WstHeading2Font),
);

const primaryTextColor = createStylesParam(
  'primaryTextColor',
  colorParam(StyleType.color, 'color-5', CssColor.WstParagraph2Color),
);

const primaryTextFont = createStylesParam(
  'primaryTextFont',
  fontParam('Body-M', { size: 14 }, CssFont.WstParagraph2Font),
);

const secondaryTextColor = createStylesParam(
  'secondaryTextColor',
  colorParam(StyleType.color, 'color-4', CssColor.WstShade3Color),
);

const secondaryTextFont = createStylesParam(
  'secondaryTextFont',
  fontParam('Body-M', { size: 14 }, CssFont.WstParagraph2Font),
);

const borderRadius = createStylesParam(
  'borderRadius',
  numberParam(StyleType.borderRadius, 0),
);

export const styleParamsThatOverrideSettings: {
  [key in keyof (IStyleParamsBoolean & IStyleParamsVariants)]: IStyleParam;
} = {
  headerSubtitleVisibility: booleanParam(true),
  headerTitleVisibility: booleanParam(true),
  headerLocationFilterVisibility: booleanParam(true),
  headerStaffFilterVisibility: booleanParam(true),
  headerServiceFilterVisibility: booleanParam(true),
  headerVisibility: booleanParam(true),
  headerTimezoneSelectionVisibility: booleanParam(true),
  headerSubtitleSource: numberParam(
    StyleType.UNKNOWN_CssPropertyType as any,
    undefined,
  ),
  timeslotDetailsVisibility: booleanParam(true),
  videoConferenceBadgeVisibility: booleanParam(true),
  dateAndTimeTimezoneSelectionVisibility: booleanParam(true),
};

const styleParamsDefinitions: { [key in keyof IStylesParams]: IStyleParam } = {
  topAndBottomPadding: numberParamWithMobileDefault(StyleType.padding, 24, 48),
  sidePadding: {
    inheritDesktop: false,
    ...numberParam(StyleType.padding, 20),
  },
  widgetBorderWidth: numberParam(StyleType.borderWidth, 0),
  widgetBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  widgetBorderRadius: numberParam(StyleType.borderRadius, 0),
  widgetBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  headerBackgroundColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-1',
    0,
    CssColor.WstPrimaryBackgroundColor,
  ),
  dividerWidth: numberParam(StyleType.borderWidth, 1),
  dividerColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  sectionsGap: {
    inheritDesktop: false,
    ...numberParam(StyleType.gap, 44),
  },
  pageTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  pageTitleFont: fontParam('Page-title', { size: 32 }, CssFont.WstHeading2Font),
  sectionTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  sectionTitleFont: fontParam(
    'Page-title',
    { size: 20 },
    CssFont.WstHeading2Font,
  ),
  primaryTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  primaryTextFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  secondaryTextColor: colorParam(
    StyleType.color,
    'color-4',
    CssColor.WstShade3Color,
  ),
  secondaryTextFont: fontParam(
    'Body-M',
    { size: 14 },
    CssFont.WstParagraph2Font,
  ),
  actionColor: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  disabledColor: colorParam(
    StyleType.color,
    'color-3',
    CssColor.WstSystemDisabledColor,
  ),
  borderWidth: numberParam(StyleType.borderWidth, 1),
  borderRadius: numberParam(StyleType.borderRadius, 0),
  dateTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  dateTextFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  timeSelectionTextFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  timeSelectionTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  timeSelectionBackgroundColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-1',
    0,
    CssColor.WstPrimaryBackgroundColor,
  ),
  bookButtonTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstButtonPrimaryColor,
  ),
  bookButtonBGColor: colorParam(
    StyleType.backgroundColor,
    'color-8',
    CssColor.WstPrimaryBackgroundColor,
  ),
  bookButtonHoverBGColor: colorParam(
    StyleType.backgroundColor,
    'color-8',
    CssColor.WstPrimaryBackgroundColor,
  ),
  bookButtonBorderColor: colorParam(
    StyleType.borderColor,
    'color-8',
    CssColor.WstPrimaryBackgroundColor,
  ),
  bookButtonHoverBorderColor: colorParam(
    StyleType.borderColor,
    'color-8',
    CssColor.WstPrimaryBackgroundColor,
  ),
  bookButtonHoverTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstButtonPrimaryColor,
  ),
  bookButtonTextFont: fontParamWithMobileDefault(
    'Body-M',
    14,
    16,
    CssFont.WstParagraph2Font,
  ),
  slotPrimaryTextFont,
  slotPrimaryTextColor,
  slotNameTextFont: fontRefParam(
    slotPrimaryTextFont,
    'slotPrimaryTextFont',
    CssFont.WstParagraph2Font,
  ),
  slotNameTextColor: colorRefParam(
    slotPrimaryTextColor,
    'slotPrimaryTextColor',
    CssColor.WstParagraph2Color,
  ),
  slotTimeTextFont: fontRefParam(
    slotPrimaryTextFont,
    'slotPrimaryTextFont',
    CssFont.WstParagraph2Font,
  ),
  slotTimeTextColor: colorRefParam(
    slotPrimaryTextColor,
    'slotPrimaryTextColor',
    CssColor.WstParagraph2Color,
  ),
  slotSecondaryTextFont: fontParam(
    'Body-M',
    { size: 14 },
    CssFont.WstParagraph2Font,
  ),
  slotSecondaryTextColor: colorParam(
    StyleType.color,
    'color-4',
    CssColor.WstShade3Color,
  ),
  mobilePageTitleFontSize: numberParam(StyleType.fontSize, 32),
  mobileSectionTitleFontSize: numberParam(StyleType.fontSize, 20),
  mobilePrimaryTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileSecondaryTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileDateTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileTimeSelectionTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileBookButtonTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileSlotPrimaryTextFontSize,
  mobileSlotNameTextFontSize: fontSizeRefParam(
    mobileSlotPrimaryTextFontSize,
    'mobileSlotPrimaryTextFontSize',
  ),
  mobileSlotTimeTextFontSize: fontSizeRefParam(
    mobileSlotPrimaryTextFontSize,
    'mobileSlotPrimaryTextFontSize',
  ),
  mobileSlotSecondaryTextFontSize: numberParam(StyleType.fontSize, 14),
  slotBackgroundColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-1',
    0,
    CssColor.WstPrimaryBackgroundColor,
  ),
  slotBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.3,
    CssColor.WstSystemLine1Color,
  ),
  slotBorderWidth: numberParam(StyleType.borderWidth, 1),
  servicePrimaryTextColor0: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  servicePrimaryTextColor1: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  servicePrimaryTextColor2: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  servicePrimaryTextColor3: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  servicePrimaryTextColor4: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  servicePrimaryTextColor5: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  servicePrimaryTextColor6: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  servicePrimaryTextColor7: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  servicePrimaryTextColor8: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  servicePrimaryTextColor9: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  serviceBackgroundColor0: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
    CssColor.WstPrimaryBackgroundColor,
  ),
  serviceBackgroundColor1: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
    CssColor.WstPrimaryBackgroundColor,
  ),
  serviceBackgroundColor2: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
    CssColor.WstPrimaryBackgroundColor,
  ),
  serviceBackgroundColor3: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
    CssColor.WstPrimaryBackgroundColor,
  ),
  serviceBackgroundColor4: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
    CssColor.WstPrimaryBackgroundColor,
  ),
  serviceBackgroundColor5: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
    CssColor.WstPrimaryBackgroundColor,
  ),
  serviceBackgroundColor6: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
    CssColor.WstPrimaryBackgroundColor,
  ),
  serviceBackgroundColor7: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
    CssColor.WstPrimaryBackgroundColor,
  ),
  serviceBackgroundColor8: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
    CssColor.WstPrimaryBackgroundColor,
  ),
  serviceBackgroundColor9: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    0.1,
    CssColor.WstPrimaryBackgroundColor,
  ),
  dataAndTimeDividerColor: colorRefParam(
    dividerColor,
    'dividerColor',
    CssColor.WstSystemLine1Color,
  ),
  serviceDetailsDividerColor: colorRefParam(
    dividerColor,
    'dividerColor',
    CssColor.WstSystemLine1Color,
  ),
  dataAndTimeDividerWidth: numberRefParam(
    dividerWidth,
    'dividerWidth',
    StyleType.borderWidth,
  ),
  serviceDetailsDividerWidth: numberRefParam(
    dividerWidth,
    'dividerWidth',
    StyleType.borderWidth,
  ),
  dateAndTimeSectionTitleColor: colorRefParam(
    sectionTitleColor,
    'sectionTitleColor',
    CssColor.WstHeading2Color,
  ),
  dateAndTimeSectionTitleFont: fontRefParam(
    sectionTitleFont,
    'sectionTitleFont',
    CssFont.WstHeading2Font,
  ),
  serviceDetailsSectionTitleColor: colorRefParam(
    sectionTitleColor,
    'sectionTitleColor',
    CssColor.WstHeading2Color,
  ),
  serviceDetailsSectionTitleFont: fontRefParam(
    sectionTitleFont,
    'sectionTitleFont',
    CssFont.WstHeading2Font,
  ),
  dateAndTimePrimaryTextColor: colorRefParam(
    primaryTextColor,
    'primaryTextColor',
    CssColor.WstParagraph2Color,
  ),
  dateAndTimePrimaryTextFont: fontRefParam(
    primaryTextFont,
    'primaryTextFont',
    CssFont.WstParagraph2Font,
  ),
  dateAndTimeSecondaryTextColor: colorRefParam(
    secondaryTextColor,
    'secondaryTextColor',
    CssColor.WstShade3Color,
  ),
  dateAndTimeSecondaryTextFont: fontRefParam(
    secondaryTextFont,
    'secondaryTextFont',
    CssFont.WstParagraph2Font,
  ),
  serviceDetailsPrimaryTextColor: colorRefParam(
    primaryTextColor,
    'primaryTextColor',
    CssColor.WstParagraph2Color,
  ),
  serviceDetailsPrimaryTextFont: fontRefParam(
    primaryTextFont,
    'primaryTextFont',
    CssFont.WstParagraph2Font,
  ),
  serviceDetailsSecondaryTextColor: colorRefParam(
    secondaryTextColor,
    'secondaryTextColor',
    CssColor.WstShade3Color,
  ),
  serviceDetailsSecondaryTextFont: fontRefParam(
    secondaryTextFont,
    'secondaryTextFont',
    CssFont.WstParagraph2Font,
  ),
  bookButtonCornerRadius: numberRefParam(
    borderRadius,
    'borderRadius',
    StyleType.borderRadius,
  ),
  priceBeforeDiscountColor: colorParam(
    StyleType.color,
    'color-3',
    CssColor.WstSystemDisabledColor,
  ),
  priceBeforeDiscountFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  discountNameColor: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstButtonPrimaryColor,
  ),
  discountNameFont: fontParam(
    'Body-M',
    { size: 14 },
    CssFont.WstParagraph2Font,
  ),
  mobilePriceBeforeDiscountFontSize: numberParam(StyleType.fontSize, 14),
  mobileDiscountNameFontSize: numberParam(StyleType.fontSize, 14),
  ...styleParamsThatOverrideSettings,
};

export default createStylesParams<IStylesParams>(styleParamsDefinitions);

export const customCssVars: CustomCssVarsFn = (args) => {
  return generateCustomCssVars(args, settingsParamsDefinitions);
};
